import React from 'react'
import styled from 'styled-components'
import { Button } from 'antd'
import { SearchCheckbox } from './SearchCheckbox'
import { themeGet } from '@styled-system/theme-get'

import Image from 'next/image'
import { FilterItemType, FilterMap } from '@/modules/products/viewModels/ProductListViewModel'
import { useRouter } from 'next/router'
import {
	CATEGORY_HANDOUT_PAGE,
	CATEGORY_TEXTBOOK_PAGE,
	CATEGORY_WORKBOOK_PAGE,
} from '@/settings/constant'
import { ActiveLink } from '@/components/ActiveLink'
import { includesPath } from '@/lib/utils/url'
import { observer } from 'mobx-react-lite'
import { nanoid } from '@solvook/nanoid'

const FilterWrapper = styled.div`
	display: flex;
	flex-direction: column;
	padding: 16px 0;

	&.scroll {
		&::-webkit-scrollbar {
			width: 4px;
			background-color: #efefef;
		}

		&::-webkit-scrollbar-thumb {
			border-radius: 2px;
			background: #bbbbbf;
		}
	}

	@media (max-width: 1200px) {
		padding: 20px 0;

		&:nth-of-type(1) {
			padding-top: 0;
		}
	}
	@media (max-width: 768px) {
	}

	.ant-btn {
		height: auto;
	}

	@media (hover: hover) {
		&:hover {
			background-color: transparent;
		}
	}

	&:not(:nth-last-of-type(1)),
	.check-area:not(:nth-last-of-type(1)) {
		border-bottom: 1px solid ${themeGet('border.1', '#E8E8EA')};

		@media (max-width: 1200px) {
			border: none;
		}
		@media (max-width: 768px) {
			position: relative;

			&:before {
				display: block;
				content: '';
				position: absolute;
				left: -24px;
				bottom: 0;
				width: 100vw;
				height: 1px;
				background-color: ${themeGet('border.1', '#E8E8EA')};
			}
		}
	}

	.check-area {
		padding-bottom: 16px;
		margin-bottom: 16px;

		.spread {
			.ant-checkbox-group {
				max-height: 1000px;

				@media (max-width: 1200px) {
					max-height: 1500px;
				}
			}
		}

		&:nth-of-type(1) {
			.spread .ant-checkbox-group {
				max-height: 1800px;

				@media (max-width: 1200px) {
					max-height: 2500px;
				}
			}
		}

		&:nth-of-type(2) {
			.spread .ant-checkbox-group {
				max-height: 4000px;

				@media (max-width: 1200px) {
					max-height: 4400px;
				}
			}
		}

		&:nth-of-type(5) {
			.spread .ant-checkbox-group {
				max-height: 5000px;

				@media (max-width: 1200px) {
					max-height: 6000px;
				}
			}
		}

		.ant-btn {
			padding: 0 12px;
			margin: 12px 0 0 -12px;

			@media (hover: hover) {
				&:hover {
					background-color: transparent;

					span {
						color: rgba(27, 27, 41, 0.7);
					}
				}
			}

			span {
				font-size: 14px;
				font-weight: 600;
				color: rgba(27, 27, 41, 0.3);
				margin: 0;
				transition: color 0.2s;
			}

			.anticon {
				margin-right: 8px;
			}
		}

		@media (max-width: 1200px) {
			position: relative;

			.ant-btn {
				position: absolute;
				top: -12px;
				right: -12px;
				padding: 12px;
				margin-top: 0;
			}
		}
		@media (max-width: 768px) {
		}
	}

	input {
		margin-right: 5px;
	}

	.ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
		background-color: ${themeGet('background.0', '#FDFDFF')};
	}
`

const FilterHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 24px 0 16px;
	border-bottom: 1px solid ${themeGet('border.1', '#E8E8EA')};

	.reset-btn {
		height: auto;
		padding: 0 12px;
		margin-right: -12px;

		img {
			vertical-align: text-bottom;
			margin-right: 2px;
			opacity: 0.3;
			transition: opacity 0.2s;
		}

		span {
			font-size: 14px;
			font-weight: bold;
			color: #bbbbbf;
			transition: color 0.2s;
		}

		@media (hover: hover) {
			&:hover {
				background-color: transparent;

				img {
					opacity: 1;
				}

				span {
					color: rgba(27, 27, 27, 0.7);
				}
			}
		}
	}

	@media (max-width: 1200px) {
		display: none;
	}
	@media (max-width: 768px) {
	}
`

const FilterMobileHeader = styled.div`
	display: none;
	align-items: center;
	justify-content: end;
	background-color: #fff;

	@media (max-width: 1200px) {
		display: flex;

		.ant-btn {
			width: 56px;
			height: 56px;
		}
	}
	@media (max-width: 768px) {
	}
`

const FilterSectionTitle = styled.p`
	font-size: 16px;
	font-weight: 600;
	color: ${themeGet('text.0', '#1B1B29')};
	margin: 0;

	@media (max-width: 1200px) {
		font-size: 20px;
		font-weight: bold;
		line-height: 1.5;
	}
	@media (max-width: 768px) {
		font-size: 18px;
	}
`

const FilterMenuItem = styled.ul`
	li {
		cursor: pointer;
		padding: 8px 0;
		transition: opacity 0.2s;

		&:nth-of-type(1) {
			margin-top: 8px;
		}

		&:nth-last-of-type(1) {
			margin-bottom: -8px;
		}

		.active-link {
			height: auto;

			&.active {
				div {
					font-weight: bold;
					color: ${themeGet('primary.0', '#002FA8')};
				}
			}
		}

		span {
			display: none;
		}

		@media (hover: hover) {
			&:hover {
				opacity: 0.7;
			}
		}

		@media (max-width: 1200px) {
			font-size: 18px;
			padding: 10px 0 10px 8px;

			&:nth-of-type(1) {
				margin-top: 12px;
			}
		}
		@media (max-width: 768px) {
			font-size: 16px;
		}
	}
`

const menuFilters: { label: string; value: string; path: string }[] = [
	{
		label: '수업자료',
		value: '1',
		path: CATEGORY_HANDOUT_PAGE,
	},
	{
		label: '교과서',
		value: '2',
		path: CATEGORY_TEXTBOOK_PAGE,
	},
	{
		label: '참고서',
		value: '3',
		path: CATEGORY_WORKBOOK_PAGE,
	},
]

const SearchResultFilterWrapper = styled.div`
	.ant-checkbox-wrapper {
		display: flex;
		margin-inline-start: 0;

		.ant-checkbox {
			display: inline-block;
			vertical-align: middle;
			width: 16px;
			height: 16px;

			&.ant-checkbox-checked {
				.ant-checkbox-inner {
					background-color: #002fa8;
				}
			}

			input {
				border-radius: 0;
			}

			.ant-checkbox-inner {
				border: 1px solid ${themeGet('border.1', '#E8E8EA')};
				border-radius: 2px;
			}
		}
	}

	.filter-content-box {
		@media (max-width: 1200px) {
			overflow-y: auto;
			overflow-x: hidden;
			max-height: calc(100vh - 56px);
			padding: 24px 24px 112px;
		}
		@media (max-width: 768px) {
		}
	}
`

export interface SearchResultFilterProps {
	dataSource: any
	onShowModal: (item: string[], idx: number) => void
	onShowMobileFilter: () => void
	onReset: () => void
	// filterCheckboxList: FilterListType
	selectedItems: FilterMap
	onChangeFilterItems: (filter: string, items: FilterItemType[]) => void
	deleteItem?: any
	resetFilter?: boolean
	category?: string
	maxShowItemNum?: number
}

export const renderFilterName = (name: string) => {
	let renderString: string

	switch (name) {
		case 'curriculum':
			renderString = '학년'
			break
		case 'subject':
			renderString = '과목'
			break
		case 'publisher':
			renderString = '출판사'
			break
		case 'license':
			renderString = '라이선스'
			break
		case 'based_textbook':
			renderString = '출처 교과서'
			break
		case 'based_workbook':
			renderString = '출처 참고서'
			break
		case 'unit':
			renderString = '단원/UNIT'
			break
		case 'handout_category':
			renderString = '수업자료 유형'
			break
		case 'author':
			renderString = '수업자료 브랜드'
			break
		default:
			renderString = ''
			break
	}

	return renderString
}

export const SearchResultFilter: React.FC<SearchResultFilterProps> = observer(
	({
		dataSource,
		onShowModal,
		onShowMobileFilter,
		onReset,
		selectedItems,
		onChangeFilterItems,
		deleteItem,
		resetFilter,
		maxShowItemNum = 3,
	}) => {
		const router = useRouter()
		const { asPath } = router

		return (
			<SearchResultFilterWrapper>
				<FilterHeader>
					<FilterSectionTitle>필터</FilterSectionTitle>
					<Button
						className={'reset-btn'}
						onClick={onReset}
						type="text"
						icon={
							<Image
								src={'/static/assets/icon_reset.svg'}
								alt={'초기화'}
								width={16}
								height={16}
							/>
						}>
						초기화
					</Button>
				</FilterHeader>
				<FilterMobileHeader>
					<Button
						type="text"
						icon={
							<Image
								src={'/static/assets/icon_close_m.svg'}
								alt={'닫기'}
								width={24}
								height={24}
							/>
						}
						onClick={onShowMobileFilter}></Button>
				</FilterMobileHeader>
				<div className="filter-content-box">
					<FilterWrapper>
						<FilterSectionTitle>카테고리</FilterSectionTitle>
						<FilterMenuItem>
							{menuFilters &&
								menuFilters.map((item, idx) => (
									<li key={'filter_' + idx}>
										<ActiveLink
											className={
												includesPath(asPath, item.path)
													? 'active-link active'
													: 'active-link'
											}
											href={`${item.path}`}>
											<div>{item.label}</div>
										</ActiveLink>
									</li>
								))}
						</FilterMenuItem>
					</FilterWrapper>

					{dataSource && (
						<FilterWrapper className={'scroll'}>
							{Object.keys(dataSource).map((filterKey, idx) => {
								const tempList = dataSource[filterKey]
								const uid = nanoid()

								return (
									<div key={'category_' + uid} className={'check-area'}>
										<FilterSectionTitle>
											{renderFilterName(filterKey)}
										</FilterSectionTitle>
										<SearchCheckbox
											filterKey={filterKey}
											options={tempList}
											selectedItems={selectedItems}
											onChange={onChangeFilterItems}
											maxShowItemNum={maxShowItemNum}
										/>
									</div>
								)
							})}
						</FilterWrapper>
					)}
				</div>
			</SearchResultFilterWrapper>
		)
	},
)
//
